import { use, useCallback } from "react";
import type { WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../api/adminApi";
import type { ObjectDuplicatesResponse } from "../../api/types/objectTypes";
import ObjectCardChecksContext from "./ObjectCardChecksContext";
import { duplicateWarningActions } from "./objectDuplicates";

interface ObjectToCheck {
  objectType: string;
  objectId: string;
  action?: WorkflowSignalType;
}

export interface ObjectDuplicates {
  response?: ObjectDuplicatesResponse;
  hasFailed: boolean;
  isFetching: boolean;
}

interface ObjectDuplicatesResult extends ObjectDuplicates {
  refetch: () => Promise<void>;
}

const useObjectDuplicates = (objectToCheck: ObjectToCheck | undefined): ObjectDuplicatesResult => {
  const cardDuplicates = use(ObjectCardChecksContext)?.duplicates;

  const objectType = objectToCheck?.objectType ?? "";
  const objectId = objectToCheck?.objectId ?? "";
  const shouldCheck =
    objectToCheck !== undefined &&
    (objectToCheck.action === undefined || duplicateWarningActions.includes(objectToCheck.action));
  const usesCardDuplicates =
    shouldCheck &&
    cardDuplicates?.objectType === objectType &&
    cardDuplicates.objectId === objectId &&
    !cardDuplicates.hasFailed;

  const getDuplicates = useCallback(() => adminApi.getObjectDuplicates(objectType, objectId), [objectId, objectType]);

  const [response, fetchError, { isFetching, fetch: refetch }] = useFetch(getDuplicates, {
    disabled: !shouldCheck || usesCardDuplicates,
  });

  if (fetchError) {
    logError(fetchError, "[useObjectDuplicates] getObjectDuplicates");
  }

  if (usesCardDuplicates) {
    return { ...cardDuplicates, refetch };
  }

  return { response, hasFailed: shouldCheck && Boolean(fetchError), isFetching, refetch };
};

export default useObjectDuplicates;
